import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ResponsiveContainer, ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine,
} from "recharts";
import {
  Target, Scale, GitCompareArrows, FlaskConical, Combine, ClipboardCheck, Play, ShieldQuestion, Plus, FileDown, Layers, BookOpenCheck, Route, Search,
} from "lucide-react";
import { api, useFetch } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Panel, StatusChip, KpiTile, LoadingState, EmptyState } from "@/components/app/primitives";
import { LiveBadge, ConfidenceRank, fmtInt, fmtPct, fmtPt } from "@/components/app/livePrimitives";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const GRID = "rgba(233,238,245,0.08)";
const AXIS = "#7F8A99";
const TT_STYLE = { backgroundColor: "#11161D", border: "1px solid #243041", borderRadius: 8, color: "#E9EEF5", fontSize: 12 };

const CONF_TONE = { high: "validated", medium: "ai", low: "risk", insufficient: "danger" };

const CURVE_COLORS = ["#C43C3C", "#2AA7A1", "#C9922A", "#7F8A99"];

// ---- Governed metric catalog ----

const fmtMetricValue = (m) => {
  const v = m.value;
  if (v == null) return "—";
  if (typeof v === "number") {
    return m.key.includes("rate") || m.key.includes("share") ? `${(v * 100).toFixed(2)}%` : v.toLocaleString();
  }
  return null; // dict-valued: rendered as breakdown
};

const MetricCard = ({ m }) => {
  const [open, setOpen] = useState(false);
  const scalar = fmtMetricValue(m);
  const needsValidation = m.status === "requires_denominator_validation";
  return (
    <div className={`rounded-xl border p-3 ${needsValidation ? "border-[--amber] bg-[--amber-soft]" : "border-[--hairline] bg-[--shell-2]"}`} data-testid={`metric-card-${m.key}`}>
      <button onClick={() => setOpen(!open)} className="w-full text-left">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-[--tx-1] leading-snug">{m.label}</span>
          {needsValidation
            ? <StatusChip tone="risk" className="ml-auto shrink-0">Requires denominator validation</StatusChip>
            : scalar && <span className="ml-auto text-sm tabular-nums font-bold text-[--tx-1] shrink-0">{scalar}</span>}
        </div>
        {m.trend?.prior_month && (
          <div className="text-[10px] font-mono text-[--tx-3] mt-0.5">
            vs {m.trend.prior_month.month}: <span className={m.trend.prior_month.delta > 0 ? "text-[--danger]" : "text-[--green]"}>
              {m.trend.prior_month.delta > 0 ? "+" : ""}{typeof m.value === "number" && (m.key.includes("rate") ? `${(m.trend.prior_month.delta * 100).toFixed(2)}pt` : m.trend.prior_month.delta.toLocaleString())}
            </span>
            {m.trend.prior_year && <span className="ml-2">vs {m.trend.prior_year.month}: {m.trend.prior_year.delta > 0 ? "+" : ""}{m.key.includes("rate") ? `${(m.trend.prior_year.delta * 100).toFixed(2)}pt` : m.trend.prior_year.delta.toLocaleString()}</span>}
          </div>
        )}
        {typeof m.value === "object" && m.value != null && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {Object.entries(m.value).slice(0, 6).map(([k, v]) => (
              <span key={k} className="rounded bg-[--panel-dark] border border-[--divider] px-1.5 py-0.5 text-[10px] font-mono text-[--tx-2]">
                {k}: {typeof v === "number" ? `${(v * 100).toFixed(1)}%` : (v?.cancel_rate != null ? `${(v.cancel_rate * 100).toFixed(1)}%` : "—")}
              </span>
            ))}
          </div>
        )}
      </button>
      {open && (
        <div className="mt-2 pt-2 border-t border-[--divider] space-y-1 text-[10px] font-mono text-[--tx-3]" data-testid={`metric-detail-${m.key}`}>
          <div><span className="text-[--amber] uppercase tracking-wider">Formula</span> <span className="text-[--tx-2]">{m.formula}</span></div>
          <div>numerator: <span className="text-[--tx-2]">{m.numerator}</span> · denominator: <span className="text-[--tx-2]">{m.denominator}</span></div>
          <div>timeframe: <span className="text-[--tx-2]">{m.timeframe}</span> · population: <span className="text-[--tx-2]">{m.population}</span></div>
          <div>sources: <span className="text-[--tx-2]">{(m.source_tables || []).join(", ")}</span></div>
          <div>required: <span className="text-[--tx-2]">{(m.required_fields || []).join(", ")}</span> · v{m.version} · validated {m.last_validated}</div>
          {m.caveats?.length > 0 && <div className="text-[--amber]">caveats: {m.caveats.join(" | ")}</div>}
        </div>
      )}
    </div>
  );
};

// ---- Order journey lookup ----

const OrderJourneyPanel = () => {
  const [q, setQ] = useState("");
  const [matches, setMatches] = useState([]);
  const [journey, setJourney] = useState(null);
  const [busy, setBusy] = useState(false);

  const search = async (val) => {
    setQ(val);
    setJourney(null);
    if (val.length < 2) { setMatches([]); return; }
    try {
      const res = await api.get(`/live/journey/search?q=${encodeURIComponent(val)}`);
      setMatches(res.data.matches || []);
    } catch (e) { setMatches([]); }
  };

  const openJourney = async (oid) => {
    try {
      setBusy(true);
      const res = await api.get(`/live/journey/${encodeURIComponent(oid)}`);
      setJourney(res.data);
      setMatches([]);
    } catch (e) { toast.error("Journey lookup failed"); }
    setBusy(false);
  };

  return (
    <Panel title="Order journey view - order-level assembly" testId="forensic-journey-panel"
      right={<Route size={12} className="text-[--tx-3]" />}>
      <div className="relative max-w-md">
        <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[--tx-3]" />
        <Input value={q} onChange={(e) => search(e.target.value)} placeholder="Search an order ID (e.g. AL11...)"
          data-testid="journey-search-input"
          className="h-8 pl-8 text-xs bg-[--shell-3] border-[--hairline]" />
        {matches.length > 0 && (
          <div className="absolute z-20 mt-1 w-full rounded-lg border border-[--hairline] bg-[--shell-2] shadow-xl overflow-hidden" data-testid="journey-search-results">
            {matches.map((mt) => (
              <button key={mt.order_id} onClick={() => openJourney(mt.order_id)}
                data-testid={`journey-match-${mt.order_id}`}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-white/[0.04] transition-colors">
                <span className="text-xs font-mono text-[--tx-1]">{mt.order_id}</span>
                <span className="text-[10px] font-mono text-[--tx-3] ml-auto">{mt.channel} · {mt.cancel_month}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {busy && <LoadingState message="Assembling order journey..." />}
      {journey?.found && (
        <div className="mt-3" data-testid="journey-result">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <span className="text-sm font-semibold text-[--tx-1] font-mono">{journey.order_id}</span>
            <StatusChip tone="ai">{journey.coverage}</StatusChip>
            <StatusChip tone="neutral">{journey.datasets_joined} dataset{journey.datasets_joined === 1 ? "" : "s"} joined</StatusChip>
          </div>
          <div className="text-[10px] font-mono text-[--tx-3] mb-3">sources: {journey.sources.join(" · ")}</div>
          <div className="space-y-0" data-testid="journey-timeline">
            {journey.stages.map((s, i) => (
              <div key={s.key} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <div className={`w-2.5 h-2.5 rounded-full shrink-0 mt-1 ${s.available ? "bg-[--ai]" : "bg-[--shell-3] border border-[--hairline]"}`} />
                  {i < journey.stages.length - 1 && <div className="w-px flex-1 bg-[--divider]" />}
                </div>
                <div className={`pb-3 min-w-0 ${s.available ? "" : "opacity-50"}`}>
                  <div className="text-xs font-semibold text-[--tx-1]">{s.label}</div>
                  {s.available ? (
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] font-mono text-[--tx-3] mt-0.5">
                      {Object.entries(s.fields).slice(0, 6).map(([k, v]) => (
                        <span key={k}>{k}: <span className="text-[--tx-2]">{String(v).slice(0, 36)}</span></span>
                      ))}
                    </div>
                  ) : (
                    <div className="text-[10px] font-mono text-[--tx-3] mt-0.5">no data - needs: {s.missing_fields.slice(0, 4).join(", ")}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
          {journey.note && <p className="text-[11px] text-[--tx-3] leading-relaxed mt-1">{journey.note}</p>}
        </div>
      )}
      {journey && !journey.found && <div className="mt-3 text-xs text-[--amber]" data-testid="journey-not-found">{journey.message}</div>}
    </Panel>
  );
};

const CohortSection = ({ cohorts }) => {
  const ct = cohorts.carryover_test || {};
  // Build merged chart data: x = age bucket, one series per cohort curve
  const buckets = cohorts.curves?.[0]?.curve?.map((p) => p.bucket) || [];
  const chart = buckets.map((b, i) => {
    const row = { bucket: b };
    cohorts.curves.forEach((c) => {
      const v = c.curve[i]?.cum_cancel_rate;
      row[c.label] = v != null ? +(v * 100).toFixed(2) : null;
    });
    return row;
  });
  const recentVintages = (cohorts.vintages || []).filter((v) => v.vintage >= "2026-04").slice(0, 12);
  const cancelsOnly = cohorts.basis === "share_of_cancels";
  return (
    <div data-testid="cohort-section">
      <div className="text-[10px] font-mono text-[--tx-3] mb-2">
        {cohorts.total_orders.toLocaleString()} canonical orders · sources: {cohorts.sources.join(" · ")}
      </div>
      {cancelsOnly && cohorts.caveat && (
        <div className="rounded-lg border border-[--amber] bg-[--amber-soft] p-2.5 mb-3" data-testid="cohort-basis-caveat">
          <div className="text-[10px] font-mono uppercase tracking-wider text-[--amber] mb-1">Basis: share of cohort cancels</div>
          <p className="text-[11px] text-[--tx-2] leading-relaxed">{cohorts.caveat}</p>
        </div>
      )}
      <div className="grid lg:grid-cols-2 gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-[--ai] mb-2">
            {cancelsOnly ? "Cumulative share of cohort cancels by order age" : "Cumulative cancel rate by order age"}
          </div>
          <div className="h-[210px]">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chart} margin={{ top: 6, right: 10, bottom: 0, left: -12 }}>
                <CartesianGrid stroke={GRID} vertical={false} />
                <XAxis dataKey="bucket" tick={{ fill: AXIS, fontSize: 9 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: AXIS, fontSize: 9 }} axisLine={false} tickLine={false} unit="%" />
                <Tooltip contentStyle={TT_STYLE} />
                {cohorts.curves.map((c, i) => (
                  <Line key={c.label} type="monotone" dataKey={c.label} stroke={CURVE_COLORS[i % CURVE_COLORS.length]} strokeWidth={2} dot={{ r: 2 }} connectNulls />
                ))}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
            {cohorts.curves.map((c, i) => (
              <span key={c.label} className="inline-flex items-center gap-1 text-[10px] text-[--tx-3]">
                <span className="w-2 h-0.5 rounded" style={{ backgroundColor: CURVE_COLORS[i % CURVE_COLORS.length] }} />
                {c.label} ({c.orders.toLocaleString()})
              </span>
            ))}
          </div>
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-[--amber] mb-2">Carry-over test · {ct.metric}</div>
          <div className="rounded-lg border border-[--divider] bg-[--panel-dark] p-3 mb-2">
            {(ct.reasons || []).map((r) => (
              <div key={r} className="text-[11px] text-[--tx-2] leading-relaxed mb-1">• {r}</div>
            ))}
            <div className="text-[9px] font-mono text-[--tx-3] mt-1.5">
              Thresholds: min sample {ct.thresholds?.min_sample?.toLocaleString()} · relative ≥ {ct.thresholds?.relative_factor}x · absolute ≥ {(ct.thresholds?.absolute_pt * 100).toFixed(0)}pt
            </div>
          </div>
          <div className="overflow-x-auto thin-scroll">
            <table className="w-full text-[10px]" data-testid="cohort-vintage-table">
              <thead>
                <tr className="text-left text-[9px] font-mono uppercase tracking-wider text-[--tx-3]">
                  <th className="py-1 pr-2">Vintage</th><th className="py-1 pr-2">Group</th>
                  <th className="py-1 pr-2 text-right">Orders</th><th className="py-1 pr-2 text-right">Cancels</th>
                  <th className="py-1 text-right">Rate</th>
                </tr>
              </thead>
              <tbody>
                {recentVintages.map((v, i) => (
                  <tr key={`${v.vintage}-${v.group}-${i}`} className="border-t border-[--divider] text-[--tx-2]">
                    <td className="py-1 pr-2 font-mono">{v.vintage}</td>
                    <td className="py-1 pr-2">{v.group}</td>
                    <td className="py-1 pr-2 text-right tabular-nums">{v.orders.toLocaleString()}</td>
                    <td className="py-1 pr-2 text-right tabular-nums">{v.cancels.toLocaleString()}</td>
                    <td className="py-1 text-right tabular-nums text-[--tx-1]">{v.cancel_rate != null ? `${(v.cancel_rate * 100).toFixed(1)}%` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

const MetricIntegrityPanel = ({ def, gate, reconciliation, onSaved }) => {
  const { roleKey } = useApp();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const fields = ["numerator", "denominator", "population", "as_of_date", "cohort_basis", "maturity_rule", "inclusions", "exclusions"];

  const startEdit = () => { setForm(Object.fromEntries(fields.map((f) => [f, def?.[f] || ""]))); setEditing(true); };

  const declare = async (asReconciled = false) => {
    try {
      await api.put("/live/metric-definition", {
        ...form, actor_role: roleKey,
        status: asReconciled ? "reconciled" : "declared",
      });
      toast.success(asReconciled ? "Definition declared and marked reconciled" : "Metric definition declared");
      setEditing(false);
      onSaved();
    } catch (e) { toast.error("Save failed"); }
  };

  const statusTone = { unconfirmed: "risk", declared: "ai", reconciled: "validated" }[def?.status || "unconfirmed"];
  const canReconcile = reconciliation?.status === "consistent";

  return (
    <Panel title="Metric integrity panel" testId="forensic-integrity-panel"
      right={
        <div className="flex items-center gap-2">
          <StatusChip tone={statusTone} className="capitalize" data-testid="integrity-status-chip">{def?.status || "unconfirmed"}</StatusChip>
          {!editing && (
            <button onClick={startEdit} data-testid="integrity-edit-button"
              className="rounded-lg border border-[--hairline] bg-[--shell-3] px-2.5 py-1 text-[11px] font-semibold text-[--tx-2] hover:text-[--tx-1] transition-colors">
              {def?.status === "unconfirmed" ? "Confirm definition" : "Edit"}
            </button>
          )}
        </div>
      }>
      {!editing ? (
        <div className="grid md:grid-cols-2 gap-2.5">
          {fields.map((f) => (
            <div key={f} className="rounded-lg bg-[--shell-2] border border-[--divider] px-3 py-2">
              <div className="text-[10px] font-mono uppercase tracking-wider text-[--tx-3] mb-0.5">{f.replace(/_/g, " ")}</div>
              <div className="text-[12px] text-[--tx-2] leading-snug">{def?.[f] || "—"}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-2.5">
          {fields.map((f) => (
            <div key={f}>
              <div className="text-[10px] font-mono uppercase tracking-wider text-[--tx-3] mb-1">{f.replace(/_/g, " ")}</div>
              {f === "as_of_date" ? (
                <Input value={form[f]} onChange={(e) => setForm((x) => ({ ...x, [f]: e.target.value }))}
                  className="h-8 text-xs bg-[--shell-3] border-[--hairline]" data-testid={`integrity-input-${f}`} />
              ) : (
                <Textarea value={form[f]} onChange={(e) => setForm((x) => ({ ...x, [f]: e.target.value }))}
                  className="text-xs bg-[--shell-3] border-[--hairline] min-h-[52px]" data-testid={`integrity-input-${f}`} />
              )}
            </div>
          ))}
          <div className="flex items-center justify-end gap-2 pt-1">
            <button onClick={() => setEditing(false)} className="rounded-lg border border-[--hairline] px-3 py-2 text-xs text-[--tx-2] hover:text-[--tx-1] transition-colors">Cancel</button>
            <button onClick={() => declare(false)} data-testid="integrity-declare-button"
              className="rounded-lg bg-[--six-crimson] hover:bg-[--six-crimson-hover] px-3.5 py-2 text-xs font-semibold text-white active:scale-[0.98] transition-colors">
              Declare definition
            </button>
            {canReconcile && (
              <button onClick={() => declare(true)} data-testid="integrity-reconcile-button"
                className="rounded-lg bg-[--green] hover:brightness-110 px-3.5 py-2 text-xs font-semibold text-white active:scale-[0.98] transition-colors">
                Declare + mark reconciled
              </button>
            )}
          </div>
        </div>
      )}
      <div className={`mt-3 rounded-lg border p-3 text-xs leading-relaxed ${gate?.level === "conclusive" ? "border-[--green] bg-[--green-soft] text-[--tx-1]" : "border-[--amber] bg-[--amber-soft] text-[--tx-1]"}`} data-testid="forensic-gate-banner">
        <ShieldQuestion size={12} className="inline mr-1.5" />{gate?.message}
      </div>
    </Panel>
  );
};

export default function AugustForensicPage() {
  const { roleKey } = useApp();
  const { data, loading, refetch } = useFetch("/live/forensic");
  const { data: catalog, refetch: refetchCatalog } = useFetch("/live/metrics/catalog");
  const [running, setRunning] = useState(false);
  const [dim, setDim] = useState(null);
  const [focusMonth, setFocusMonth] = useState("2026-08");
  const [showAddHyp, setShowAddHyp] = useState(false);
  const [hypForm, setHypForm] = useState({ title: "", statement: "", disproving_condition: "" });

  const priorOf = (m) => {
    const y = +m.slice(0, 4), mm = +m.slice(5, 7);
    return mm === 1 ? `${y - 1}-12` : `${y}-${String(mm - 1).padStart(2, "0")}`;
  };

  const runCalc = async () => {
    setRunning(true);
    try {
      await api.post("/live/calc/run", { actor_role: roleKey, focus: focusMonth, comparison: priorOf(focusMonth) });
      toast.success(`Calculation pass complete - ${focusMonth} vs ${priorOf(focusMonth)}`);
      refetch(); refetchCatalog();
    } catch (e) { toast.error("Calculation failed"); }
    setRunning(false);
  };

  const addHyp = async () => {
    try {
      await api.post("/live/hypotheses", { ...hypForm, actor_role: roleKey });
      toast.success("Hypothesis registered");
      setShowAddHyp(false); setHypForm({ title: "", statement: "", disproving_condition: "" });
      refetch();
    } catch (e) { toast.error("Could not add hypothesis"); }
  };

  const chartData = useMemo(() => (data?.monthly || []).map((m) => ({
    month: m.month.slice(2).replace("-", "/"),
    rate: m.pdf_rate != null ? +(m.pdf_rate * 100).toFixed(1) : (m.xlsx_rate != null ? +(m.xlsx_rate * 100).toFixed(1) : null),
    cancels: m.pdf_cancels ?? m.xlsx_cancels,
    provisional: m.month === "2026-08",
  })), [data]);

  const dims = useMemo(() => (data?.segments || []).map((a) => a.dimension), [data]);
  const activeDim = dim || (dims.includes("channel") ? "channel" : dims[0]);
  const activeAnalysis = (data?.segments || []).find((a) => a.dimension === activeDim);

  const d2dSignal = useMemo(() => {
    const ch = (data?.segments || []).find((a) => a.dimension === "channel" || a.dimension === "Channel");
    return ch?.segments?.find((s) => s.segment === "D2D");
  }, [data]);

  if (loading) return <LoadingState message="Assembling forensic workspace..." />;

  const noData = !data?.calc_run;

  return (
    <div data-testid="live-forensic-page" className="space-y-5">
      <PageHeader
        kicker="Live engagement · analysis"
        title="August Cancellation Forensic"
        subtitle="What changed, where it changed, why it changed, and which actions follow. Every number below is calculated from ingested Brightspeed files under a declared metric definition - AI never computes a metric."
        right={
          <div className="flex items-center gap-2">
            <LiveBadge />
            <Select value={focusMonth} onValueChange={setFocusMonth}>
              <SelectTrigger className="h-8 w-[120px] text-xs bg-[--shell-3] border-[--hairline]" data-testid="forensic-month-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[--shell-2] border-[--hairline]">
                {(catalog?.months || ["2026-08"]).slice().reverse().map((m) => (
                  <SelectItem key={m} value={m} className="text-xs">{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/live/export/executive-packet.pdf`}
              data-testid="forensic-packet-button"
              className="inline-flex items-center gap-1.5 rounded-lg border border-[--hairline] bg-[--shell-3] px-3.5 py-2 text-xs font-semibold text-[--tx-2] hover:text-[--tx-1] hover:bg-[#243041] active:scale-[0.98] transition-colors">
              <FileDown size={12} /> Executive packet
            </a>
            <button onClick={runCalc} disabled={running} data-testid="forensic-run-calc-button"
              className="inline-flex items-center gap-1.5 rounded-lg bg-[--six-crimson] hover:bg-[--six-crimson-hover] disabled:opacity-50 active:scale-[0.98] px-3.5 py-2 text-xs font-semibold text-white transition-colors">
              <Play size={12} /> {running ? "Calculating..." : "Run calculation pass"}
            </button>
          </div>
        }
      />

      {/* Success standard */}
      <div className="rounded-xl border border-[--six-crimson] bg-[--six-crimson-soft] p-4 flex items-start gap-3" data-testid="forensic-success-standard">
        <Target size={16} className="text-[#E86A6A] shrink-0 mt-0.5" />
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#E86A6A] mb-1">Success standard</div>
          <p className="text-[13px] text-[--tx-1] leading-relaxed">{data?.success_standard}</p>
        </div>
      </div>

      {noData && (
        <Panel testId="forensic-no-data">
          <EmptyState message="No calculation run yet. Upload Brightspeed files in the Data Intake Hub, then run a calculation pass." />
        </Panel>
      )}

      {/* 1. Metric integrity - the gate */}
      <MetricIntegrityPanel def={data?.metric_definition} gate={data?.gate} reconciliation={data?.reconciliation} onSaved={refetch} />

      {!noData && (
        <>
          {/* 2. Reconciliation */}
          <Panel title="Source reconciliation" testId="forensic-reconciliation-panel"
            right={<StatusChip tone={data.reconciliation?.status === "consistent" ? "validated" : "risk"} className="capitalize">{(data.reconciliation?.status || "").replace("_", " ")}</StatusChip>}>
            <div className="grid md:grid-cols-4 gap-2.5">
              <div className="rounded-lg bg-[--shell-2] border border-[--divider] px-3 py-2">
                <div className="text-[10px] font-mono uppercase tracking-wider text-[--tx-3]">Source A</div>
                <div className="text-[11px] text-[--tx-2] mt-0.5">{data.reconciliation?.xlsx_source || "—"}</div>
              </div>
              <div className="rounded-lg bg-[--shell-2] border border-[--divider] px-3 py-2">
                <div className="text-[10px] font-mono uppercase tracking-wider text-[--tx-3]">Source B</div>
                <div className="text-[11px] text-[--tx-2] mt-0.5">{data.reconciliation?.pdf_source || "—"}</div>
              </div>
              <KpiTile label="Months consistent" value={`${data.reconciliation?.months_consistent}/${data.reconciliation?.months_overlapping}`} tone="validated" testId="recon-months-tile" />
              <KpiTile label="Max cancel variance" value={data.reconciliation?.max_cancel_diff != null ? fmtInt(data.reconciliation.max_cancel_diff) : "—"} sub="absolute cancels, worst month" tone="ai" />
            </div>
          </Panel>

          {/* 3. What changed */}
          <div className="grid md:grid-cols-4 gap-3" data-testid="forensic-kpi-band">
            <KpiTile label="July total rate" value={fmtPct((data.monthly.find((m) => m.month === "2026-07") || {}).pdf_rate)} sub="reconciled sources" tone="neutral" testId="kpi-jul-rate" />
            <KpiTile label="Aug full-month rate" value={fmtPct((data.monthly.find((m) => m.month === "2026-08") || {}).pdf_rate)} sub="immature - provisional by maturity rule" tone="risk" testId="kpi-aug-rate" />
            {d2dSignal && (
              <>
                <KpiTile label="D2D MTD rate delta" value={fmtPt(d2dSignal.rate_delta)} sub={`weekday-matched: ${fmtPct(d2dSignal.comp_rate)} → ${fmtPct(d2dSignal.rate)}`} tone="danger" testId="kpi-d2d-delta" />
                <KpiTile label="D2D contribution" value={d2dSignal.contribution != null ? `${Math.round(d2dSignal.contribution * 100)}%` : "—"} sub="of total MTD cancel change vs July" tone="crimson" testId="kpi-d2d-contribution" />
              </>
            )}
          </div>

          {/* 4. Monthly trend */}
          <Panel title="Cancellation rate trend - Sep 2025 to Aug 2026" testId="forensic-trend-panel"
            right={<span className="text-[10px] font-mono text-[--tx-3]">calc {data.calc_run?.calc_version} · run {(data.calc_run?.ts || "").slice(0, 16).replace("T", " ")}</span>}>
            <div className="h-[240px]">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ top: 8, right: 12, bottom: 0, left: -8 }}>
                  <CartesianGrid stroke={GRID} vertical={false} />
                  <XAxis dataKey="month" tick={{ fill: AXIS, fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis yAxisId="rate" tick={{ fill: AXIS, fontSize: 10 }} axisLine={false} tickLine={false} unit="%" domain={[0, 24]} />
                  <YAxis yAxisId="vol" orientation="right" tick={{ fill: AXIS, fontSize: 10 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={TT_STYLE} />
                  <Bar yAxisId="vol" dataKey="cancels" name="Cancels" fill="#24364A" radius={[3, 3, 0, 0]} />
                  <Line yAxisId="rate" type="monotone" dataKey="rate" name="Cancel rate %" stroke="#2AA7A1" strokeWidth={2} dot={{ r: 2.5 }} />
                  <ReferenceLine yAxisId="rate" y={15} stroke="#2EAD6B" strokeDasharray="4 4" label={{ value: "15% target", fill: "#2EAD6B", fontSize: 10, position: "insideTopRight" }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <p className="text-[10px] text-[--tx-3] mt-2">August is a partial reporting month (as-of {data.metric_definition?.as_of_date}); its full-month rate is not comparable. August comparisons use weekday-matched month-to-date counts per the declared maturity rule.</p>
          </Panel>

          {/* 5. Segment variance explorer */}
          <Panel
            title="Segment variance - ranked by contribution to the August change"
            testId="forensic-segments-panel"
            right={
              <div className="flex items-center gap-2">
                <StatusChip tone="ai">{activeAnalysis?.basis === "mtd" ? "Weekday-matched MTD basis" : "Full-month basis (provisional)"}</StatusChip>
                <Select value={activeDim || ""} onValueChange={setDim}>
                  <SelectTrigger className="w-[190px] h-8 text-xs bg-[--shell-3] border-[--hairline]" data-testid="forensic-dimension-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[--shell-2] border-[--hairline] max-h-72">
                    {dims.map((d) => <SelectItem key={d} value={d} className="text-xs capitalize">{d.replace(/_/g, " ")}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            }
          >
            {!activeAnalysis ? <EmptyState message="No segment data for this dimension." /> : (
              <div className="overflow-x-auto thin-scroll">
                <table className="w-full text-xs" data-testid="forensic-segment-table">
                  <thead>
                    <tr className="text-left text-[10px] font-mono uppercase tracking-wider text-[--tx-3]">
                      <th className="py-1.5 pr-3">Segment</th>
                      <th className="py-1.5 pr-3 text-right">Orders</th>
                      <th className="py-1.5 pr-3 text-right">Cancels</th>
                      <th className="py-1.5 pr-3 text-right">Rate</th>
                      <th className="py-1.5 pr-3 text-right">Jul rate</th>
                      <th className="py-1.5 pr-3 text-right">Δ rate</th>
                      <th className="py-1.5 pr-3 text-right">Δ volume</th>
                      <th className="py-1.5 pr-3 text-right">Share</th>
                      <th className="py-1.5 pr-3 text-right">Contribution</th>
                      <th className="py-1.5">Confidence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeAnalysis.segments.slice(0, 14).map((s) => (
                      <tr key={s.segment} className="border-t border-[--divider] hover:bg-white/[0.02]">
                        <td className="py-1.5 pr-3 text-[--tx-1] font-medium max-w-[220px] truncate">{s.segment}</td>
                        <td className="py-1.5 pr-3 text-right tabular-nums text-[--tx-2]">{fmtInt(s.orders)}</td>
                        <td className="py-1.5 pr-3 text-right tabular-nums text-[--tx-2]">{fmtInt(s.cancels)}</td>
                        <td className="py-1.5 pr-3 text-right tabular-nums text-[--tx-1]">{fmtPct(s.rate)}</td>
                        <td className="py-1.5 pr-3 text-right tabular-nums text-[--tx-3]">{fmtPct(s.comp_rate)}</td>
                        <td className={`py-1.5 pr-3 text-right tabular-nums font-semibold ${(s.rate_delta || 0) > 0 ? "text-[--danger]" : "text-[--green]"}`}>{fmtPt(s.rate_delta)}</td>
                        <td className={`py-1.5 pr-3 text-right tabular-nums ${(s.volume_delta || 0) > 0 ? "text-[--amber]" : "text-[--green]"}`}>{s.volume_delta > 0 ? "+" : ""}{fmtInt(s.volume_delta)}</td>
                        <td className="py-1.5 pr-3 text-right tabular-nums text-[--tx-2]">{fmtPct(s.share)}</td>
                        <td className="py-1.5 pr-3 text-right tabular-nums text-[--ai] font-semibold">{s.contribution != null ? `${Math.round(s.contribution * 100)}%` : "—"}</td>
                        <td className="py-1.5"><StatusChip tone={CONF_TONE[s.confidence]} className="!text-[10px]">{s.confidence}</StatusChip></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="flex items-center justify-between mt-2 pt-2 border-t border-[--divider]">
              <p className="text-[10px] text-[--tx-3]">Segments ranked by absolute cancel-volume change, not by rate alone. Lineage: live_segment_metrics · {data.calc_run?.inputs?.length} input files · calc {data.calc_run?.calc_version}.</p>
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/live/export/segments.csv`} data-testid="forensic-export-csv"
                className="text-[11px] font-semibold text-[--ai] hover:text-[#35B7B1] transition-colors shrink-0">Export CSV</a>
            </div>
          </Panel>

          {/* 6. Hypotheses */}
          <Panel title="Hypothesis register" testId="forensic-hypotheses-panel"
            right={
              <button onClick={() => setShowAddHyp(!showAddHyp)} data-testid="forensic-add-hypothesis-button"
                className="inline-flex items-center gap-1 rounded-lg border border-[--hairline] bg-[--shell-3] px-2.5 py-1 text-[11px] font-semibold text-[--tx-2] hover:text-[--tx-1] transition-colors">
                <Plus size={11} /> Register hypothesis
              </button>
            }>
            {showAddHyp && (
              <div className="rounded-lg border border-[--hairline] bg-[--shell-2] p-3 mb-3 space-y-2">
                <Input placeholder="Hypothesis title" value={hypForm.title} onChange={(e) => setHypForm((f) => ({ ...f, title: e.target.value }))}
                  className="h-8 text-xs bg-[--shell-3] border-[--hairline]" data-testid="hyp-title-input" />
                <Textarea placeholder="Statement - what do you believe is happening and why" value={hypForm.statement} onChange={(e) => setHypForm((f) => ({ ...f, statement: e.target.value }))}
                  className="text-xs bg-[--shell-3] border-[--hairline]" data-testid="hyp-statement-input" />
                <Textarea placeholder="Disproving condition - what evidence would kill this hypothesis" value={hypForm.disproving_condition} onChange={(e) => setHypForm((f) => ({ ...f, disproving_condition: e.target.value }))}
                  className="text-xs bg-[--shell-3] border-[--hairline]" data-testid="hyp-disprove-input" />
                <div className="flex justify-end">
                  <button onClick={addHyp} disabled={!hypForm.title} data-testid="hyp-save-button"
                    className="rounded-lg bg-[--six-crimson] hover:bg-[--six-crimson-hover] disabled:opacity-40 px-3 py-1.5 text-xs font-semibold text-white transition-colors">Register</button>
                </div>
              </div>
            )}
            <div className="space-y-3">
              {(data.hypotheses || []).map((h) => (
                <div key={h.id} className="rounded-xl border border-[--hairline] bg-[--shell-2] p-4" data-testid={`hypothesis-card-${h.id}`}>
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="font-mono text-[10px] text-[--tx-3]">{h.id}</span>
                    <h4 className="font-display text-[13px] font-semibold text-[--tx-1]">{h.title}</h4>
                    <ConfidenceRank rank={h.rank} />
                    {h.computed_status === "supported_signals" && <StatusChip tone="ai">Supporting signals present</StatusChip>}
                  </div>
                  <p className="text-[12px] text-[--tx-2] leading-relaxed mb-3">{h.statement}</p>
                  <div className="grid md:grid-cols-2 gap-2 mb-3">
                    {[
                      ["Population", h.population], ["Comparison", h.comparison_population],
                      ["Expected pattern if true", h.expected_pattern], ["What would disprove it", h.disproving_condition],
                      ["Minimum sample", h.min_sample?.toLocaleString()], ["Next test", h.next_test],
                    ].filter(([, v]) => v).map(([l, v]) => (
                      <div key={l} className="rounded-lg bg-[--panel-dark] border border-[--divider] px-2.5 py-1.5">
                        <div className="text-[10px] font-mono uppercase tracking-wider text-[--amber]">{l}</div>
                        <div className="text-[11px] text-[--tx-2] leading-snug mt-0.5">{v}</div>
                      </div>
                    ))}
                  </div>
                  {h.evidence?.length > 0 && (
                    <div className="space-y-1.5 mb-2">
                      {h.evidence.map((e, i) => (
                        <div key={e.label || i} className={`rounded-lg border p-2.5 ${e.supports === true ? "border-[--ai] bg-[--ai-soft]" : "border-[--divider] bg-[--panel-dark]"}`}>
                          <div className="flex items-center gap-2 flex-wrap">
                            <FlaskConical size={11} className="text-[--ai]" />
                            <span className="text-[11px] font-semibold text-[--tx-1]">{e.label}</span>
                            {e.supports === true && <StatusChip tone="ai" className="!text-[10px]">Computed - supports</StatusChip>}
                            <StatusChip tone={CONF_TONE[e.confidence] || "neutral"} className="!text-[10px]">{e.confidence}</StatusChip>
                          </div>
                          <div className="text-[11px] text-[--tx-2] mt-1">{e.detail}</div>
                          <div className="text-[9px] font-mono text-[--tx-3] mt-1">Lineage: {e.lineage}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  {h.data_missing?.map((m, i) => (
                    <div key={m.label || i} className="rounded-lg border border-[--amber] bg-[--amber-soft] p-2.5 mb-1.5">
                      <div className="text-[11px] font-semibold text-[--amber]">Data missing: {m.label}</div>
                      <div className="text-[11px] text-[--tx-2] mt-0.5">{m.detail}</div>
                      <div className="text-[10px] text-[--tx-2] mt-1"><span className="font-mono uppercase text-[9px] text-[--amber]">Exact request</span> — {m.request}</div>
                    </div>
                  ))}
                  <div className="text-[10px] font-mono text-[--tx-3] mt-2">Candidate owner: {h.candidate_owner || "unassigned"}</div>
                </div>
              ))}
            </div>
          </Panel>

          {/* 6b. Cohort attribution (order-level) */}
          <Panel title="Cohort attribution - order-level" testId="forensic-cohorts-panel"
            right={
              <div className="flex items-center gap-2">
                <Layers size={12} className="text-[--tx-3]" />
                {data.cohort_analysis?.available && (
                  <StatusChip tone={{ supported: "validated", disproved: "danger" }[data.cohort_analysis.carryover_test?.verdict] || "risk"} className="capitalize" data-testid="cohort-verdict-chip">
                    Carry-over: {data.cohort_analysis.carryover_test?.verdict}
                  </StatusChip>
                )}
              </div>
            }>
            {!data.cohort_analysis?.available ? (
              <div className="rounded-lg border border-[--amber] bg-[--amber-soft] p-3" data-testid="cohort-data-missing">
                <div className="text-[11px] font-semibold text-[--amber] mb-1">Data missing - cohort curves not yet constructible</div>
                <p className="text-[11px] text-[--tx-2] leading-relaxed">{data.cohort_analysis?.request}</p>
              </div>
            ) : (
              <CohortSection cohorts={data.cohort_analysis} />
            )}
          </Panel>

          {/* 6c. Governed metric catalog */}
          <Panel title="Governed metric catalog - Brightspeed definitions" testId="forensic-metric-catalog"
            right={
              <div className="flex items-center gap-2">
                <BookOpenCheck size={12} className="text-[--tx-3]" />
                {catalog?.available && <StatusChip tone={catalog.definition_status === "reconciled" ? "validated" : "risk"}>def: {catalog.definition_status}</StatusChip>}
                {catalog?.available && <StatusChip tone="ai">{catalog.focus_month}</StatusChip>}
              </div>
            }>
            {!catalog?.available ? (
              <EmptyState message={catalog?.message || "No canonical metrics yet."} />
            ) : (
              <div className="grid md:grid-cols-2 gap-2" data-testid="metric-catalog-grid">
                {catalog.metrics.map((m) => <MetricCard key={m.key} m={m} />)}
              </div>
            )}
          </Panel>

          {/* 6d. Order journey lookup */}
          <OrderJourneyPanel />

          {/* 7. Interaction effects */}
          <Panel title="Interaction & concentration scan" testId="forensic-interactions-panel"
            right={<Combine size={12} className="text-[--tx-3]" />}>
            <div className="grid lg:grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-[--ai] mb-2">Material segments (computed, weekday-matched MTD)</div>
                <div className="space-y-1.5">
                  {(data.interactions?.available || []).slice(0, 8).map((r) => (
                    <div key={`${r.dimension}-${r.segment}`} className="flex items-center gap-2.5 rounded-lg border border-[--hairline] bg-[--shell-2] px-3 py-2">
                      <span className="text-[10px] font-mono text-[--tx-3] capitalize shrink-0 w-[110px] truncate">{r.dimension.replace(/_/g, " ")}</span>
                      <span className="text-xs text-[--tx-1] font-medium flex-1 min-w-0 truncate">{r.segment}</span>
                      <span className={`text-[11px] tabular-nums font-semibold shrink-0 ${(r.rate_delta || 0) > 0 ? "text-[--danger]" : "text-[--green]"}`}>{fmtPt(r.rate_delta)}</span>
                      <span className="text-[11px] tabular-nums text-[--amber] shrink-0">{r.volume_delta > 0 ? "+" : ""}{fmtInt(r.volume_delta)}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-[--amber] mb-2">True combinations - require order-level data</div>
                <div className="space-y-1.5">
                  {(data.interactions?.requires_order_level || []).map((c) => (
                    <div key={c.combo} className="rounded-lg border border-[--divider] bg-[--panel-dark] px-3 py-2">
                      <div className="text-xs font-medium text-[--tx-1]">{c.combo}</div>
                      <div className="text-[10px] text-[--tx-3] mt-0.5">Request: {c.request}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Panel>

          {/* 8. Action candidates */}
          <Panel title="Action candidates - evidence-gated" testId="forensic-actions-panel"
            right={<ClipboardCheck size={12} className="text-[--tx-3]" />}>
            <div className="grid lg:grid-cols-2 gap-3">
              {(data.action_candidates || []).map((a) => (
                <div key={a.id} className="rounded-xl border border-[--hairline] bg-[--shell-2] p-4" data-testid={`action-candidate-${a.id}`}>
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="font-mono text-[10px] text-[--tx-3]">{a.id}</span>
                    <h4 className="font-display text-[13px] font-semibold text-[--tx-1]">{a.title}</h4>
                    <StatusChip tone={a.kind === "validation" ? "ai" : "risk"} className="capitalize">{a.kind.replace("_", " ")}</StatusChip>
                    {a.requires_triad && <StatusChip tone="crimson">Requires Triad gate</StatusChip>}
                  </div>
                  <p className="text-[11px] text-[--tx-2] leading-relaxed mb-2.5">{a.driver}</p>
                  <div className="grid grid-cols-2 gap-1.5 text-[11px]">
                    {[
                      ["Population", a.target_population], ["Owner", a.owner],
                      ["Mechanism", a.expected_mechanism], ["Leading indicator", a.leading_indicator],
                      ["Lagging measure", a.lagging_measure], ["Baseline", a.baseline],
                      ["Target", a.target], ["Timing", a.timing],
                      ["Risk", a.risk], ["Stop rule", a.stop_rule],
                    ].map(([l, v]) => (
                      <div key={l}>
                        <div className="text-[9px] font-mono uppercase tracking-wider text-[--tx-3]">{l}</div>
                        <div className="text-[--tx-2] leading-snug">{v}</div>
                      </div>
                    ))}
                  </div>
                  {a.dependencies?.length > 0 && (
                    <div className="mt-2 text-[10px] text-[--tx-3]">Dependencies: {a.dependencies.join(" · ")}</div>
                  )}
                </div>
              ))}
            </div>
          </Panel>
        </>
      )}
    </div>
  );
}
